import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http'
import { DTOAdapter } from '@services/DTOAdapter'
import { Organisation } from '@app/domain/Organisation'
import { Business } from '@app/domain/Business'
import { Venue } from '@app/domain/Venue'
import { map, Observable, tap } from 'rxjs'
import { environment } from '@environments/environment'
import { ProductLibrary } from '@app/domain/ProductLibrary'
import { ProductLibraryDTO } from '@services/DTO/ProductLibraryDTO'
import { Voucher } from '@app/domain/Voucher'
import { VoucherDTO } from '@services/DTO/VoucherDTO'
import { ConsumableDTO, MenuDTO } from '@app/services/DTO/ConsumableDTO'
import { Consumable, Menu } from '@app/domain/Consumable'
import { ModifierGroup } from '@app/domain/Modifier'
import { ModifierGroupDTO } from '@app/services/DTO/ModifierDTO'

@Injectable({
    providedIn: 'root'
})
export class ProductLibraryService {

    constructor(
        private http: HttpClient,
        private dtoAdapter: DTOAdapter
    ) { }

    getProductLibrary(
        organisation: Organisation,
        business: Business,
        venue: Venue
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            '/product-library'
        const url = new URL(path, environment.apiBaseURL)
        return this.http.get<ProductLibraryDTO>(url.toString())
            .pipe(
                map(dto => this.dtoAdapter.adaptProductLibraryDto(dto))
            )
    }

    addProductLibrary(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            '/product-library'
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptProductLibrary(productLibrary)
        return this.http.post<ProductLibraryDTO>(url.toString(), dto)
            .pipe(
                map(dto => this.dtoAdapter.adaptProductLibraryDto(dto))
            )
    }

    addConsumable(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        consumable: Consumable
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            '/consumable'
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptConsumable(consumable)
        return this.http.post<ConsumableDTO>(url.toString(), dto)
            .pipe(
                map(dto => this.dtoAdapter.adaptConsumableDto(dto)),
                tap(consumable => productLibrary.addConsumable(consumable)),
                map(() => productLibrary)
            )
    }

    updateConsumable(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        consumable: Consumable,
        updatedConsumable: Consumable
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/consumable/${consumable.id}`
        const url = new URL(path, environment.apiBaseURL)
        const updatedDto = this.dtoAdapter.adaptConsumable(updatedConsumable)
        return this.http.put<ConsumableDTO>(url.toString(), updatedDto)
            .pipe(
                map(dto => this.dtoAdapter.adaptConsumableDto(dto)),
                tap(consumable => productLibrary.updateConsumable(consumable)),
                map(() => productLibrary)
            )
    }

    addVoucher(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        voucher: Voucher
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            '/voucher'
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptVoucher(voucher)
        return this.http.post<VoucherDTO>(url.toString(), dto)
            .pipe(
                map(dto => this.dtoAdapter.adaptVoucherDto(dto)),
                tap(voucher => productLibrary.addVoucher(voucher)),
                map(() => productLibrary)
            )
    }

    updateVoucher(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        voucher: Voucher,
        updatedVoucher: Voucher
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/voucher/${voucher.id}`
        const url = new URL(path, environment.apiBaseURL)
        const updatedDto = this.dtoAdapter.adaptVoucher(updatedVoucher)
        return this.http.put<VoucherDTO>(url.toString(), updatedDto)
            .pipe(
                map(dto => this.dtoAdapter.adaptVoucherDto(dto)),
                tap(voucher => productLibrary.updateVoucher(voucher)),
                map(() => productLibrary)
            )
    }

    updateProductImage(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        product: Consumable,
        image: File
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/consumable/${product.id}/image`
        const url = new URL(path, environment.apiBaseURL)
        const formData = new FormData()
        formData.append('image', image)
        return this.http.put<ConsumableDTO>(url.toString(), formData)
            .pipe(
                map(dto => this.dtoAdapter.adaptConsumableDto(dto)),
                tap(updatedProduct => productLibrary.updateConsumable(updatedProduct)),
                map(() => productLibrary)
            )
    }

    removeProductImage(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        product: Consumable
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/consumable/${product.id}/image`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<void>(url.toString())
            .pipe(
                tap(() => {
                    product.imageId = null
                    productLibrary.updateConsumable(product)
                }),
                map(() => productLibrary)
            )
    }

    updateVoucherImage(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        voucher: Voucher,
        image: File
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/voucher/${voucher.id}/image`
        const url = new URL(path, environment.apiBaseURL)
        const formData = new FormData()
        formData.append('image', image)
        return this.http.put<VoucherDTO>(url.toString(), formData)
            .pipe(
                map(dto => this.dtoAdapter.adaptVoucherDto(dto)),
                tap(updatedVoucher => productLibrary.updateVoucher(updatedVoucher)),
                map(() => productLibrary)
            )
    }

    removeVoucherImage(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        voucher: Voucher
    ): Observable<ProductLibrary> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/voucher/${voucher.id}/image`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<void>(url.toString())
            .pipe(
                tap(() => {
                    voucher.removeImage()
                    productLibrary.updateVoucher(voucher)
                }),
                map(() => productLibrary)
            )
    }

    // Menu methods that work with ProductLibrary
    addMenu(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        menu: Menu
    ): Observable<Menu> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            '/menu'
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptMenu(menu)
        return this.http.post<MenuDTO>(url.toString(), dto)
            .pipe(
                map(dto => this.dtoAdapter.adaptMenuDto(dto)),
                tap(created => productLibrary.addMenu(created))
            )
    }

    updateMenu(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        menu: Menu,
        updatedMenu: Menu
    ): Observable<Menu> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/menu/${menu.id}`
        const url = new URL(path, environment.apiBaseURL)
        const updatedDto = this.dtoAdapter.adaptMenu(updatedMenu)
        return this.http.put<MenuDTO>(url.toString(), updatedDto)
            .pipe(
                map(dto => this.dtoAdapter.adaptMenuDto(dto)),
                tap(updated => productLibrary.updateMenu(updated))
            )
    }

    deleteMenu(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        menu: Menu
    ): Observable<void> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/menu/${menu.id}`
        const url = new URL(path, environment.apiBaseURL)
        return this.http.delete<void>(url.toString())
    }

    // Modifier group methods that work with ProductLibrary
    addModifierGroup(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        group: ModifierGroup
    ): Observable<ModifierGroup> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            '/modifier-group'
        const url = new URL(path, environment.apiBaseURL)
        const dto = this.dtoAdapter.adaptModifierGroup(group)
        return this.http.post<ModifierGroupDTO>(url.toString(), dto)
            .pipe(
                map(dto => this.dtoAdapter.adaptModifierGroupDto(dto))
            )
    }

    updateModifierGroup(
        organisation: Organisation,
        business: Business,
        venue: Venue,
        productLibrary: ProductLibrary,
        group: ModifierGroup,
        updatedGroup: ModifierGroup
    ): Observable<ModifierGroup> {
        const path = `/organisation/${organisation.id}` +
            `/business/${business.id}` +
            `/venue/${venue.id}` +
            `/product-library/${productLibrary.id}` +
            `/modifier-group/${group.id}`
        const url = new URL(path, environment.apiBaseURL)
        const updatedDto = this.dtoAdapter.adaptModifierGroup(updatedGroup)
        return this.http.put<ModifierGroupDTO>(url.toString(), updatedDto)
            .pipe(
                map(dto => this.dtoAdapter.adaptModifierGroupDto(dto))
            )
    }
}
